import { useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";

import { SparklesIcon } from "lucide-react";

import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useFuenteDeLaDescripcion } from "@/hooks/useFuenteDeLaDescripcion";
import { useClasesDeSuelo } from "@/hooks/useClasesDeSuelo";
import { useRellenarSuelosConIa, useResolverElRelleno } from "@/hooks/useRellenoConIa";
import { getApiErrorMessage } from "@/lib/api";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import type { FuenteDeLaPropuesta as Fuente } from "@/lib/rellenoConIa";
import {
  FOTOGRAFIAS_DE_SUELOS,
  IMAGENES_DE_SUELOS,
  urlDeLaFotografia,
  urlDeLaImagenDelExpediente,
  type FotografiaDeInforme,
  type GuardarSuelosInput,
  type Informe,
  type TipoDeFotografiaDelInforme,
  type TipoDeImagenDelInforme,
} from "@/lib/informes";

import { BarraDeGuardado } from "./BarraDeGuardado";
import { DescripcionDeSuelos } from "./DescripcionDeSuelos";
import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { VisorDeFotos } from "./FotosDeTerreno";
import { FuenteDeLaPropuesta } from "./FuenteDeLaPropuesta";
import { GaleriaDeFotografias } from "./GaleriaDeFotografias";
import { NotasDeLaPestana } from "./NotasDeLaPestana";
import {
  AvisoDeLoRellenado,
  BotonDeRellenoConIa,
  type AvisoDelRelleno,
} from "./RellenoConIa";
import { TablaDeClasesDeSuelo, type FilaDeSuelo } from "./TablaDeClasesDeSuelo";
import { contarCambios, seccionDeSuelos } from "./cuadroDeSuelos";
import { fotosDelVisor } from "./registroDeTerreno";
import type { EstadoDelTerreno } from "./terrenoDeAntecedentes";
import { TarjetaDeImagen, motivoDeRechazoDeLaImagen } from "./TarjetaDeImagen";
import { resumenDeLoPropuestoEnSuelos, sumarLaPropuestaDeSuelos } from "./suelosConIa";

/**
 * La bajada de cada tarjeta de imagen, tal como la dibuja el diseño. No sale de
 * `IMAGENES_DE_SUELOS.ayuda`: aquel texto es el del tooltip de la tarjeta
 * antigua y dice otra cosa.
 */
const BAJADA_DE_LA_IMAGEN: Record<string, string> = {
  mosaico_suelos:
    "Imagen con las clases de uso dibujadas sobre el predio. Se imprime en el informe.",
  plano_regulador: "Imagen del plano regulador comunal que afecta al predio.",
};

/** El texto del recuadro vacío, tal como lo rotula el diseño. */
const MARCADOR_DE_LA_IMAGEN: Record<string, string> = {
  mosaico_suelos: "Mosaico de clases de uso · JPG, PNG o WEBP",
  plano_regulador: "Plano regulador · JPG, PNG o WEBP",
};

/** Lo que la pantalla puede pedirle a la pestaña desde el aviso de salida. */
export interface PestanaDeSuelosHandle {
  /** Guarda, igual que apretar el botón de la pestaña. */
  guardar: () => void;
  /** Toma lo que hay en pantalla como "lo guardado" y apaga el aviso. */
  marcarGuardado: () => void;
}

/** El texto de la pestaña mientras se edita. */
interface BorradorDeSuelos {
  descripcionSuelos: string;
  descripcionSuelosValidada: boolean;
  topografia: string;
  topografiaValidada: boolean;
  descripcionPlanoRegulador: string;
  filas: FilaDeSuelo[];
  notas: string;
}

interface PestanaDeSuelosProps {
  informe: Informe;
  /**
   * Lo que el encargado sincronizó desde la App de terreno. Solo se LEE: propone,
   * no corrige. Sin él (módulo apagado) la pestaña se dibuja sin ninguna pieza de
   * terreno.
   */
  terreno?: EstadoDelTerreno;
  /** Quién tiene asignada la visita: da el nombre cuando todavía no sincroniza. */
  asignado?: string | null;
  /** Abre la pestaña "Registro de terreno". */
  onVerTerreno?: () => void;
  guardando: boolean;
  /** Qué imagen o bloque tiene una subida o un borrado en curso, o `null`. */
  ocupadaEn: string | null;
  ref?: Ref<PestanaDeSuelosHandle>;
  /** Avisa si hay cambios sin guardar en esta pestaña. */
  onCambios?: (hayCambios: boolean) => void;
  onImagenDelExpediente: (tipo: TipoDeImagenDelInforme, archivo: File) => void;
  onQuitarImagenDelExpediente: (tipo: TipoDeImagenDelInforme) => void;
  /** Si devuelve una promesa, varias fotos seguidas se suben una tras otra. */
  onFotografia: (tipo: TipoDeFotografiaDelInforme, archivo: File) => void | Promise<unknown>;
  onQuitarFotografia: (fotografiaId: number) => void;
  onGuardar: (input: GuardarSuelosInput) => void;
}

/**
 * La pestaña "Descripción de suelos": la prosa que describe el suelo y la
 * topografía, el cuadro de clases de uso, las fotos del terreno, el mosaico y
 * el plano regulador.
 *
 * LAS IMÁGENES SE GUARDAN SOLAS, al soltarlas, y el botón "Guardar cambios"
 * escribe solo TEXTO — misma división que "Descripción de la propiedad". Una
 * imagen es una petición con su archivo: meterla en el guardado del formulario
 * obligaría a reenviarlas todas cada vez que alguien corrige una coma. Por eso
 * tampoco cuentan como cambios sin guardar: cuando ese aviso aparece, ya están
 * en el servidor.
 *
 * DOS CASILLAS "Información validada" Y NO TRES: la glosa del plano regulador
 * no la lleva, y sale del prototipo. La casilla existe porque el texto lo
 * puede haber propuesto la IA, y de los tres solo esos dos ofrecen "Mejorar
 * redacción" — una casilla sobre un texto que nadie puede autogenerar pediría
 * revisar lo que la persona acaba de escribir.
 *
 * LA CASILLA DEL CUADRO ES POR FILA, no una al pie: ver
 * {@see TablaDeClasesDeSuelo}.
 *
 * LO DE TERRENO SE LEE SIN ESCRIBIR NADA: las dos descripciones traen, al lado,
 * lo que el encargado grabó para ese campo, y "Redactar desde terreno" propone
 * un texto que entra al formulario como cambio sin guardar.
 *
 * "GUARDAR CAMBIOS" VIVE EN LA BARRA FIJA del pie, con el conteo de lo que está
 * sin guardar: las imágenes y las fotos no cuentan, ya están en el servidor.
 */
export function PestanaDeSuelos({
  informe,
  terreno,
  asignado = null,
  onVerTerreno,
  guardando,
  ocupadaEn,
  ref,
  onCambios,
  onImagenDelExpediente,
  onQuitarImagenDelExpediente,
  onFotografia,
  onQuitarFotografia,
  onGuardar,
}: PestanaDeSuelosProps) {
  /* El catálogo va en su propia consulta y su fracaso NO voltea la pestaña:
     sin él no se dibuja ningún selector de clase y el resto se sigue viendo
     entero. Una pantalla que desaparece porque falló un control esconde lo que
     el informe SÍ tiene guardado — mismo criterio que la escala de
     homologación en la ficha. */
  const catalogo = useClasesDeSuelo();

  const [borrador, setBorrador] = useState<BorradorDeSuelos>(() => borradorDesde(informe));
  const [base, setBase] = useState<BorradorDeSuelos>(() => borradorDesde(informe));

  /* Los rechazos del NAVEGADOR (tipo y peso), por recuadro. No son los del
     servidor —esos llegan como toast desde la pantalla, que es quien llama a
     la API—: son los que evitan mandar 10 MB por la red para enterarse
     después. La clave es la misma que `ocupadaEn`. */
  const [rechazos, setRechazos] = useState<Record<string, string>>({});

  const rellenar = useRellenarSuelosConIa(informe.id);
  const resolverElRelleno = useResolverElRelleno();

  const [aviso, setAviso] = useState<AvisoDelRelleno | null>(null);
  const [errorDelRelleno, setErrorDelRelleno] = useState<string | null>(null);

  /**
   * La corrida en curso y qué FILAS trajo.
   *
   * SOLO LAS FILAS, no los textos: "descartar" quita lo que la IA agregó al
   * cuadro y deja los dos párrafos donde están. Deshacer un texto que se
   * escribió sobre un campo vacío es ambiguo —¿vuelve a vacío, o a lo que había
   * antes de que la persona lo editara?— y el caso corriente es querer sacar las
   * filas que no corresponden, no perder la prosa que ya se leyó.
   */
  const [ultimoRelleno, setUltimoRelleno] = useState<{
    ejecucionId: number;
    claves: string[];
    /** De qué documento dice la IA que salió cada fila, por clave. */
    fuentes: Record<string, Fuente | null>;
  } | null>(null);

  // La fuente de cada TEXTO que la IA escribió: vale mientras el campo siga
  // igual a lo propuesto. Ver `useFuenteDeLaDescripcion`.
  const fuenteDeLaDescripcion = useFuenteDeLaDescripcion(borrador.descripcionSuelos);
  const fuenteDeLaTopografia = useFuenteDeLaDescripcion(borrador.topografia);

  /**
   * El borrador de AHORA, para el `onSuccess` del relleno.
   *
   * LEER `borrador` AHÍ SERÍA EL DE HACE VARIOS SEGUNDOS: leer el expediente
   * tarda, y quien apretó el botón sigue escribiendo mientras espera. Es el
   * mismo defecto que se corrigió en la pestaña de aguas, con su test.
   */
  const borradorAhora = useRef(borrador);
  borradorAhora.current = borrador;

  const cambios = contarCambios(borrador, base);
  const hayCambios = cambios > 0;

  useEffect(() => {
    onCambios?.(hayCambios);
  }, [hayCambios, onCambios]);

  useEffect(() => () => onCambios?.(false), [onCambios]);

  useImperativeHandle(ref, () => ({
    guardar,
    marcarGuardado: () => {
      setBase(borrador);

      // EL GUARDADO ES LO QUE ACEPTA LA PROPUESTA, y no el clic que la trajo:
      // hasta acá era un borrador que la persona podía descartar. Se avisa y se
      // OLVIDA la corrida, para que un segundo guardado no vuelva a aceptarla.
      if (ultimoRelleno) {
        resolverElRelleno.mutate({
          ejecucionId: ultimoRelleno.ejecucionId,
          resultado: "aceptado",
        });
        setUltimoRelleno(null);
        setAviso(null);
      }

      fuenteDeLaDescripcion.olvidar();
      fuenteDeLaTopografia.olvidar();
    },
  }));

  function pedirElRelleno() {
    setErrorDelRelleno(null);
    setAviso(null);

    rellenar.mutate(undefined, {
      onSuccess: ({ ejecucion_id, propuesta, documentos, pendientes, sin_usar }) => {
        const actual = borradorAhora.current;
        const mezcla = sumarLaPropuestaDeSuelos(actual, propuesta);

        escribir({
          filas: mezcla.filas,
          topografia: mezcla.topografia,
          descripcionSuelos: mezcla.descripcionSuelos,
        });

        setUltimoRelleno({
          ejecucionId: ejecucion_id,
          // Solo las filas NUEVAS: las que ya estaban son de la persona.
          claves: mezcla.filas.slice(actual.filas.length).map((fila) => fila.clave),
          fuentes: mezcla.fuentesPorClave,
        });

        // Un relleno nuevo reemplaza lo anterior: solo vale la fuente de lo que
        // ESTA corrida escribió.
        fuenteDeLaTopografia.olvidar();
        fuenteDeLaDescripcion.olvidar();

        if (mezcla.topografiaFuente !== undefined) {
          fuenteDeLaTopografia.registrar(mezcla.topografia, mezcla.topografiaFuente);
        }

        if (mezcla.descripcionFuente !== undefined) {
          fuenteDeLaDescripcion.registrar(mezcla.descripcionSuelos, mezcla.descripcionFuente);
        }

        setAviso({
          resumen: resumenDeLoPropuestoEnSuelos(
            mezcla.clasesAgregadas,
            mezcla.topografiaEscrita,
            mezcla.descripcionEscrita,
          ),
          documentos,
          pendientes,
          sinUsar: sin_usar,
        });
      },

      // El error se muestra ACÁ y no en un toast: habla de esta pestaña y de lo
      // que hay que hacer para resolverlo —adjuntar el avalúo, esperar a que se
      // transcriba un escaneo—, y un toast se va antes de terminar de leerlo.
      onError: (e) =>
        setErrorDelRelleno(
          getApiErrorMessage(
            e,
            "No pudimos leer el expediente. Inténtalo de nuevo o completa la pestaña a mano.",
          ),
        ),
    });
  }

  /**
   * Quita las FILAS que trajo el último relleno y lo anota como descartado.
   *
   * Los dos textos no se tocan: ver el comentario de `ultimoRelleno`.
   *
   * UN DESCARTE INFORMA TANTO COMO UNA ACEPTACIÓN: sin él, `ia_ejecuciones` solo
   * tendría los casos en que la IA acertó, y esa proporción es lo único que
   * permite decidir si la función vale lo que cuesta.
   */
  function descartarElRelleno() {
    if (!ultimoRelleno) return;

    const propuestas = new Set(ultimoRelleno.claves);

    escribir({ filas: borrador.filas.filter((fila) => !propuestas.has(fila.clave)) });

    resolverElRelleno.mutate({
      ejecucionId: ultimoRelleno.ejecucionId,
      resultado: "descartado",
    });

    setUltimoRelleno(null);
    setAviso(null);
    fuenteDeLaDescripcion.olvidar();
    fuenteDeLaTopografia.olvidar();
  }

  function escribir(cambio: Partial<BorradorDeSuelos>) {
    setBorrador((actual) => ({ ...actual, ...cambio }));
  }

  function elegirImagen(
    clave: string,
    archivo: File,
    subir: (archivo: File) => void | Promise<unknown>,
  ) {
    const motivo = motivoDeRechazoDeLaImagen(archivo);

    setRechazos((actuales) => ({ ...actuales, [clave]: motivo ?? "" }));

    if (motivo === null) return subir(archivo);
  }

  function guardar() {
    onGuardar({
      // Vaciar un textarea BORRA el texto: `""` viaja como `null`, que es lo
      // que la columna guarda cuando no hay prosa.
      descripcion_general: textoONulo(borrador.descripcionSuelos),
      descripcion_general_validada: borrador.descripcionSuelosValidada,
      topografia: textoONulo(borrador.topografia),
      topografia_validada: borrador.topografiaValidada,
      descripcion_plano_regulador: textoONulo(borrador.descripcionPlanoRegulador),
      // Las filas SIN clase elegida no viajan: el servidor exige la clase, así
      // que mandarlas devolvería un 422 sobre una fila que la persona dejó a
      // medio empezar. Se descartan en silencio, igual que una fila vacía.
      clases: borrador.filas
        .filter((fila) => fila.sueloClaseId !== null)
        .map((fila) => ({
          // Las recién agregadas no llevan `id`: el servidor las crea.
          ...(fila.id !== undefined ? { id: fila.id } : {}),
          suelo_clase_id: fila.sueloClaseId as number,
          descripcion: textoONulo(fila.descripcion),
          superficie: textoONulo(fila.superficie),
          validada: fila.validada,
        })),
      notas_observaciones: textoONulo(borrador.notas),
    });
  }

  const unidad = informe.unidad_superficie ?? "ha";

  /* --- Lo de terreno: el visor de fotos de la sección --- */

  const registro = terreno?.estado === "ok" ? terreno.registro : undefined;
  const seccion = seccionDeSuelos(registro);
  const [fotoAbierta, setFotoAbierta] = useState<string | null>(null);
  const fotosDelRegistro = useMemo(
    () => (seccion ? fotosDelVisor([seccion], "todo") : []),
    [seccion],
  );

  /** Lo escribió un modelo: nadie lo revisó, y la fuente anterior ya no es la de este texto. */
  function textoRedactado(campo: "descripcionSuelos" | "topografia", texto: string) {
    if (campo === "descripcionSuelos") {
      fuenteDeLaDescripcion.olvidar();
      escribir({ descripcionSuelos: texto, descripcionSuelosValidada: false });
    } else {
      fuenteDeLaTopografia.olvidar();
      escribir({ topografia: texto, topografiaValidada: false });
    }
  }

  /** Vuelve la pestaña a lo último guardado y suelta lo que la IA propuso. */
  function descartarLosCambios() {
    setBorrador(base);

    if (ultimoRelleno) {
      resolverElRelleno.mutate({ ejecucionId: ultimoRelleno.ejecucionId, resultado: "descartado" });
      setUltimoRelleno(null);
    }

    setAviso(null);
    fuenteDeLaDescripcion.olvidar();
    fuenteDeLaTopografia.olvidar();
  }

  return (
    <div className="flex flex-col gap-3.5">
      <section className="border-success-dot/25 bg-success-soft/50 flex flex-wrap items-center gap-3.5 rounded-xl border-[1.5px] px-4 py-3">
        <span className="bg-success-soft text-success-soft-foreground flex size-8.5 flex-none items-center justify-center rounded-lg">
          <SparklesIcon className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-ink text-[0.8125rem] font-bold">Completar suelos con IA</h3>
          <p className="text-ink-muted mt-0.5 text-[0.71875rem] font-semibold text-pretty">
            Lee los antecedentes publicados y el registro de terreno, y propone las descripciones
            y las clases de uso que falten. No valida nada.
          </p>
        </div>
        <div className="flex-none">
          <BotonDeRellenoConIa
            rellenando={rellenar.isPending}
            disabled={guardando}
            variante="oscura"
            onRellenar={pedirElRelleno}
          />
        </div>
      </section>

      <AvisoDeLoRellenado
        rellenando={rellenar.isPending}
        error={errorDelRelleno}
        aviso={aviso}
        onDescartar={ultimoRelleno ? descartarElRelleno : null}
      />

      <DescripcionDeSuelos
        informeId={informe.id}
        numero="01"
        idDelTitulo="suelos-descripcion-titulo"
        titulo="Descripción general de los suelos"
        bajada="Serie, textura, profundidad y drenaje."
        idDelCampo="descripcion-suelos"
        etiqueta="Descripción general de los suelos"
        etiquetaDeLaValidacion="Información validada de la descripción de los suelos"
        placeholder="Serie de suelo, textura, profundidad, drenaje…"
        campoConAsistencia={CAMPOS_CON_ASISTENCIA.descripcionSuelos}
        campoDeTerreno="suelos"
        value={borrador.descripcionSuelos}
        onChange={(descripcionSuelos) => escribir({ descripcionSuelos })}
        validada={borrador.descripcionSuelosValidada}
        onValidada={(descripcionSuelosValidada) => escribir({ descripcionSuelosValidada })}
        disabled={guardando}
        fuente={
          fuenteDeLaDescripcion.visible ? (
            <FuenteDeLaPropuesta fuente={fuenteDeLaDescripcion.visible.fuente} />
          ) : null
        }
        terreno={terreno}
        asignado={asignado}
        onVerTerreno={onVerTerreno}
        onAbrirFoto={setFotoAbierta}
        onRedactado={(texto) => textoRedactado("descripcionSuelos", texto)}
      />

      <DescripcionDeSuelos
        informeId={informe.id}
        numero="02"
        idDelTitulo="suelos-topografia-titulo"
        titulo="Topografía"
        bajada="Hectáreas planas versus cerros o lomajes."
        idDelCampo="topografia"
        etiqueta="Topografía (hectáreas planas vs. cerros)"
        etiquetaDeLaValidacion="Información validada de la topografía"
        placeholder="Ej.: 133 ha planas y 12 ha de lomaje con pendiente suave…"
        campoConAsistencia={CAMPOS_CON_ASISTENCIA.topografia}
        campoDeTerreno="topografia"
        value={borrador.topografia}
        onChange={(topografia) => escribir({ topografia })}
        validada={borrador.topografiaValidada}
        onValidada={(topografiaValidada) => escribir({ topografiaValidada })}
        disabled={guardando}
        fuente={
          fuenteDeLaTopografia.visible ? (
            <FuenteDeLaPropuesta fuente={fuenteDeLaTopografia.visible.fuente} />
          ) : null
        }
        terreno={terreno}
        asignado={asignado}
        onVerTerreno={onVerTerreno}
        onAbrirFoto={setFotoAbierta}
        onRedactado={(texto) => textoRedactado("topografia", texto)}
      />

      {FOTOGRAFIAS_DE_SUELOS.map(({ tipo, etiqueta, ayuda }) => (
        <GaleriaDeFotografias
          key={tipo}
          titulo={etiqueta}
          ayuda={ayuda}
          fotografias={informe.fotografias?.[tipo] ?? []}
          urlDe={(foto: FotografiaDeInforme) =>
            urlDeLaFotografia(informe.id, foto.id, foto.hash)
          }
          ocupado={ocupadaEn === tipo}
          error={rechazos[tipo] || null}
          onArchivo={(archivo) => elegirImagen(tipo, archivo, (a) => onFotografia(tipo, a))}
          onQuitar={onQuitarFotografia}
        />
      ))}

      {/* El catálogo puede no haber llegado: ahí la tabla se dibuja igual —con
          lo que el informe ya tiene— y sus selectores quedan sin opciones. */}
      <TablaDeClasesDeSuelo
        filas={borrador.filas}
        clases={catalogo.data ?? []}
        unidad={unidad}
        superficiesDeLosRoles={(informe.roles ?? []).map((rol) => rol.superficie)}
        disabled={guardando}
        fuentes={ultimoRelleno?.fuentes}
        onCambiar={(filas) => escribir({ filas })}
      />

      {IMAGENES_DE_SUELOS.map(({ tipo, etiqueta }, indice) => {
        const imagen = informe.imagenes?.[tipo] ?? null;

        return (
          <section key={tipo} className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
            <EncabezadoNumerado
              numero={`0${4 + indice}`}
              idDelTitulo={`suelos-${tipo}-titulo`}
              titulo={etiqueta}
              bajada={BAJADA_DE_LA_IMAGEN[tipo]}
              acciones={
                <span className="bg-muted text-ink-muted rounded-[0.4375rem] px-2.5 py-[0.3125rem] text-[0.65625rem] font-bold">
                  Se guarda al soltarla
                </span>
              }
            />
            <div className="mt-3.5">
              <TarjetaDeImagen
                plana
                titulo={etiqueta}
                marcador={MARCADOR_DE_LA_IMAGEN[tipo]}
                src={imagen ? urlDeLaImagenDelExpediente(informe.id, tipo, imagen.hash) : null}
                mime={imagen?.mime}
                peso={imagen?.bytes}
                ocupado={ocupadaEn === tipo}
                error={rechazos[tipo] || null}
                onArchivo={(archivo) =>
                  elegirImagen(tipo, archivo, (a) => onImagenDelExpediente(tipo, a))
                }
                onQuitar={() => onQuitarImagenDelExpediente(tipo)}
              >
                {/* La glosa del plano regulador va DENTRO de su tarjeta, igual
                    que la descripción de deslindes va dentro del recuadro de su
                    rol: describe esa imagen y no la pestaña. NO lleva casilla
                    de validación: ver el comentario de la pestaña. */}
                {tipo === "plano_regulador" && (
                  <div className="space-y-2">
                    <Label htmlFor="descripcion-plano-regulador">
                      Descripción del plano regulador
                    </Label>
                    <Textarea
                      id="descripcion-plano-regulador"
                      rows={3}
                      placeholder="Zonificación y normativa aplicable según el plano regulador…"
                      value={borrador.descripcionPlanoRegulador}
                      disabled={guardando}
                      onChange={(evento) =>
                        escribir({ descripcionPlanoRegulador: evento.target.value })
                      }
                    />
                  </div>
                )}
              </TarjetaDeImagen>
            </div>
          </section>
        );
      })}

      <section className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
        <EncabezadoNumerado
          numero="06"
          idDelTitulo="suelos-notas-titulo"
          titulo="Notas u observaciones"
          bajada="Opcional."
        />
        <div className="mt-3.5">
          <NotasDeLaPestana
            id="notas-suelos"
            value={borrador.notas}
            onChange={(notas) => escribir({ notas })}
            disabled={guardando}
            plana
          />
        </div>
      </section>

      {/* LA BARRA VA AL PIE de la pestaña, como en Construcciones. Cuenta lo
          que es de la PESTAÑA (textos, validaciones, filas del cuadro y notas);
          las imágenes y las fotos no entran porque ya están guardadas. */}
      <BarraDeGuardado
        cambios={cambios}
        enviando={guardando}
        etiquetaEnvio="Guardar cambios"
        ayuda="Las imágenes se guardan solas al soltarlas."
        onGuardar={guardar}
        onDescartar={descartarLosCambios}
      />

      {registro ? (
        <VisorDeFotos
          informeId={informe.id}
          fotos={fotosDelRegistro}
          abierta={fotoAbierta}
          onCambiar={setFotoAbierta}
        />
      ) : null}
    </div>
  );
}

/** `""` es `null`: vaciar un campo lo BORRA, no lo deja en blanco. */
function textoONulo(valor: string): string | null {
  return valor.trim() === "" ? null : valor;
}

/**
 * El estado inicial, leído de la ficha.
 *
 * `null` se vuelve `""`: un `<textarea value={null}>` pasa a no controlado y
 * React avisa por consola, además de perder lo escrito.
 */
function borradorDesde(informe: Informe): BorradorDeSuelos {
  return {
    // LA DESCRIPCIÓN GENERAL SALE DE LA SECCIÓN y la topografía de una columna
    // del informe. Son dos lugares porque el patrón de las pestañas técnicas
    // declara UNA descripción por pestaña, y de las tres prosas de esta solo la
    // primera es "la prosa sobre la lista" — ver la migración
    // `sumar_la_descripcion_de_suelos_al_informe`.
    descripcionSuelos: informe.secciones?.suelos?.descripcion_general ?? "",
    descripcionSuelosValidada:
      informe.secciones?.suelos?.descripcion_general_validada ?? false,
    topografia: informe.topografia ?? "",
    topografiaValidada: informe.topografia_validada ?? false,
    descripcionPlanoRegulador: informe.descripcion_plano_regulador ?? "",
    filas: (informe.clases_de_suelo ?? []).map((fila) => ({
      // La clave sale del `id` del servidor, que ya es único y estable; una
      // fila agregada en pantalla usa `filaDeSueloVacia()`, que genera la suya.
      clave: `guardada-${fila.id}`,
      id: fila.id,
      sueloClaseId: fila.suelo_clase_id,
      descripcion: fila.descripcion ?? "",
      superficie: fila.superficie ?? "",
      validada: fila.validada,
    })),
    notas: informe.notas?.suelos ?? "",
  };
}
